import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { WorkListSummary } from '@project/contracts'
import { Button, Field, Modal, Notice, Panel } from '../../design/index.js'
import { useApi } from '../../app/api.js'
import { ErrorBlock, Loading, messageFor } from '../../framework/feedback.js'
import { formatDate } from '../../framework/format.js'
import { ChipChoice } from './pickers.js'
import './admin.css'

/**
 * Work List Setup (WF-004): "a place to set up your work list and assign it to
 * roles and users". Each list shows whether it is enabled and since when, how
 * many assignments it has, and a Setup button.
 */
export const WorkListSetup = () => {
  const api = useApi()
  const client = useQueryClient()
  const key = ['worklistSetup', api.companyId]
  const lists = useQuery({ queryKey: key, queryFn: () => api.send<WorkListSummary[]>('/worklists?setup=true') })
  const matrix = useQuery({ queryKey: ['adminPermissions', api.companyId], queryFn: () => api.adminPermissions() })
  const users = useQuery({ queryKey: ['adminUsers', api.companyId], queryFn: () => api.adminUsers() })
  const [editing, setEditing] = useState<{ list: WorkListSummary; roles: string[]; userIds: string[] } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = useMutation({
    mutationFn: (fn: () => Promise<WorkListSummary[]>) => fn(),
    onSuccess: (next) => {
      client.setQueryData(key, next)
      void client.invalidateQueries({ queryKey: ['worklists', api.companyId] })
      setEditing(null)
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (lists.isLoading) return <Loading />
  if (lists.error) return <ErrorBlock error={lists.error} />
  const roles = [...new Map((matrix.data ?? []).map((r) => [r.role, r.roleLabel])).entries()].map(([value, label]) => ({ value, label }))

  return (
    <>
      {error ? <div style={{ margin: '12px 0' }}><Notice kind="bad">{error}</Notice></div> : null}
      {/*
        The reference's card: one row per list — its title, Enabled / Not
        Enabled and since when, how many assignments, what it shows, the
        documents it is about — and Setup on the right.
      */}
      <Panel title="Work List Setup" aside="A place to set-up your work list and assign it to roles and users.">
        <div className="worklist-rows">
          {(lists.data ?? []).map((list) => (
            <div className="worklist-row" key={list.key}>
              <div className="worklist-row__body">
                <div className="worklist-row__head">
                  <span className="worklist-row__title">{list.label}</span>
                  <span className={`chip ${list.enabled ? 'chip--good' : 'chip--warn'}`}>{list.enabled ? 'Enabled' : 'Not Enabled'}</span>
                  {list.enabledSince ? <span className="chip">{formatDate(list.enabledSince.slice(0, 10))}</span> : null}
                  <span className="helper" style={{ margin: 0 }}>
                    {`${list.enabledSince ? `enabled since ${formatDate(list.enabledSince.slice(0, 10))} · ` : ''}${list.assignments} Assignment${list.assignments === 1 ? '' : 's'}`}
                  </span>
                </div>
                <p className="worklist__blurb" style={{ margin: '4px 0 0' }}>{list.blurb}</p>
                <span className="worklist-row__doc">{list.groupLabel}</span>
              </div>
              <div className="row" style={{ gap: 8 }}>
                <Button kind="ghost" disabled={run.isPending}
                  onClick={() => run.mutate(() => api.setWorkListEnabled(list.key, !list.enabled) as Promise<WorkListSummary[]>)}>
                  {list.enabled ? 'Disable' : 'Enable'}
                </Button>
                <Button kind="ghost" onClick={() => setEditing({ list, roles: [...list.roles], userIds: [...list.userIds] })}>Setup</Button>
              </div>
            </div>
          ))}
        </div>
      </Panel>
      {editing ? (
        <Modal title={`Who works ${editing.list.label.toLowerCase()}`} wide onClose={() => setEditing(null)}
          actions={<><Button kind="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button kind="primary" disabled={run.isPending}
              onClick={() => run.mutate(() => api.send<WorkListSummary[]>(`/worklists/${editing.list.key}/assign`, { roles: editing.roles, userIds: editing.userIds }))}>Save</Button></>}>
          <p className="helper">Assign nobody and the list is for everyone. Otherwise only these roles and people see it and its count.</p>
          <Field label="Roles">
            <ChipChoice label="Roles" options={roles} value={editing.roles} onChange={(r) => setEditing({ ...editing, roles: r })} />
          </Field>
          <Field label="Users">
            <ChipChoice label="Users" options={(users.data ?? []).filter((u) => u.active).map((u) => ({ value: u.id, label: u.name }))}
              value={editing.userIds} onChange={(u) => setEditing({ ...editing, userIds: u })} />
          </Field>
        </Modal>
      ) : null}
    </>
  )
}
